import React from "react";
import {Button, Card, CardBody, CardHeader, Dropdown, Input} from "gd-react";
import {confirmAlert} from "react-confirm-alert";
import gatewayCommandSocket from "../../services/gatewayCommandSocket";
import {errorMessage, formatValue, hex, Unknown} from "./zigbeeDebugShared";
import ZigbeeCustomClusters from "./ZigbeeCustomClusters";
import "./AssetDebug.scss";

// Pi-duck only: MainPage adds the tab once the asset's gateway is known to be one.
const GET_DEVICE_METHOD = 'zigbee.getDevice';
const SET_METHOD = 'zigbee.set';

// The clusters a GridDuck device is likely to carry; anything else shows as its ID alone.
const CLUSTER_NAMES = {
	0x0000: 'Basic', 0x0001: 'Power config', 0x0003: 'Identify', 0x0004: 'Groups', 0x0005: 'Scenes',
	0x0006: 'On/Off', 0x0008: 'Level', 0x000a: 'Time', 0x0019: 'OTA', 0x0020: 'Poll control',
	0x0021: 'Green Power', 0x0402: 'Temperature', 0x0405: 'Humidity', 0x0406: 'Occupancy',
	0x0500: 'IAS zone', 0x0702: 'Metering', 0x0b04: 'Electrical', 0x0b05: 'Diagnostics', 0x1000: 'Touchlink',
};

const INTERVIEW_TONE = {SUCCESSFUL: 'good', IN_PROGRESS: 'warn', PENDING: 'warn', FAILED: 'bad'};

// herdsman's lastSeen is epoch ms.
function relativeTime(ms) {
	if (!ms) return null;
	let seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
	if (seconds < 60) return 'just now';
	if (seconds < 3600) return Math.round(seconds / 60) + 'm ago';
	if (seconds < 86400) return Math.round(seconds / 3600) + 'h ago';
	return Math.round(seconds / 86400) + 'd ago';
}

function Field({label, children, mono}) {
	let empty = children === undefined || children === null || children === '';
	return (
		<div className={'zb-field'}>
			<div className={'zb-label'}>{label}</div>
			<div className={'zb-value' + (mono ? ' mono' : '')}>{empty ? <Unknown/> : children}</div>
		</div>
	);
}

function Stat({label, value, detail, children}) {
	return (
		<div className={'zb-stat'}>
			<div className={'zb-label'}>{label}</div>
			<div className={'zb-stat-value'}>{value === undefined || value === null ? <Unknown/> : value}</div>
			{detail ? <div className={'zb-stat-detail'}>{detail}</div> : null}
			{children}
		</div>
	);
}

function ClusterChips({clusters}) {
	if (!clusters || clusters.length === 0) return <span className={'zb-muted'}>none</span>;
	return (
		<div className={'zb-chips'}>
			{clusters.map(function (c) {
				return (
					<span key={c} className={'zb-chip'}>
						{CLUSTER_NAMES[c] ? <span className={'zb-chip-name'}>{CLUSTER_NAMES[c]}</span> : null}
						<span className={'zb-chip-id'}>{hex(c)}</span>
					</span>
				);
			})}
		</div>
	);
}

function Endpoint({ep}) {
	let binds = ep.binds || [];
	let reportings = ep.configuredReportings || [];
	return (
		<div className={'zb-endpoint'}>
			<div className={'zb-endpoint-head'}>
				<span className={'zb-endpoint-title'}>Endpoint {ep.ID}</span>
				<span className={'zb-muted'}>
					{ep.profileID !== undefined ? 'Profile ' + hex(ep.profileID) : ''}
					{ep.profileID !== undefined && ep.deviceID !== undefined ? ' · ' : ''}
					{ep.deviceID !== undefined ? 'Device ' + hex(ep.deviceID) : ''}
				</span>
			</div>

			<div className={'zb-section-label'}>Input clusters</div>
			<ClusterChips clusters={ep.inputClusters}/>

			<div className={'zb-section-label'}>Output clusters</div>
			<ClusterChips clusters={ep.outputClusters}/>

			<div className={'zb-section-label'}>Binds</div>
			{binds.length === 0 ? <span className={'zb-muted'}>none</span> :
				<table className={'zb-table'}>
					<thead><tr><th>Cluster</th><th>Target</th></tr></thead>
					<tbody>
						{binds.map(function (b, i) {
							let t = b.target || {};
							return (
								<tr key={i}>
									<td>{String(b.cluster)}</td>
									<td className={'mono'}>{t.groupID !== undefined ? 'Group ' + t.groupID : t.ieeeAddr + ' · ep ' + t.endpoint}</td>
								</tr>
							);
						})}
					</tbody>
				</table>}

			<div className={'zb-section-label'}>Reporting</div>
			{/* Empty is the usual reason a device goes quiet: nothing asks it to report. */}
			{reportings.length === 0 ? <div className={'zb-note warn'}>No reporting configured on this endpoint.</div> :
				<table className={'zb-table'}>
					<thead><tr><th>Cluster</th><th>Attribute</th><th>Min</th><th>Max</th><th>Change</th></tr></thead>
					<tbody>
						{reportings.map(function (r, i) {
							return (
								<tr key={i}>
									<td>{String(r.cluster)}</td>
									<td>{String(r.attribute)}</td>
									<td>{r.minimumReportInterval}s</td>
									<td>{r.maximumReportInterval}s</td>
									<td>{r.reportableChange}</td>
								</tr>
							);
						})}
					</tbody>
				</table>}
		</div>
	);
}

// A write-only enum with a single value (clear_metering: ['clear']) is a command, not a field.
function isAction(setting) {
	return setting.type === 'enum' && !setting.readable && (setting.values || []).length === 1;
}

// Turns what the input holds into the value the converter expects, or throws with why it cannot.
export function parseSettingValue(setting, draft) {
	if (setting.type === 'numeric') {
		let value = Number(draft);
		if (draft === '' || draft === null || draft === undefined || !Number.isFinite(value)) throw new Error('Enter a number');
		if (setting.min !== undefined && value < setting.min) throw new Error('Must be at least ' + setting.min);
		if (setting.max !== undefined && value > setting.max) throw new Error('Must be at most ' + setting.max);
		return value;
	}
	if (setting.type === 'binary') return draft === 'on' ? setting.valueOn : setting.valueOff;
	if (draft === '' || draft === null || draft === undefined) throw new Error('Choose a value');
	return draft;
}

// The input's starting point: the device's current value, in the shape each input holds it.
function initialDraft(setting) {
	if (setting.type === 'binary') return setting.value === setting.valueOn ? 'on' : 'off';
	return setting.value === undefined || setting.value === null ? '' : String(setting.value);
}

export class SettingRow extends React.Component {

	constructor(props) {
		super(props);
		this.state = {draft: initialDraft(props.setting), error: null};
		this.apply = this.apply.bind(this);
		this.confirmAction = this.confirmAction.bind(this);
	}

	// Returns the promise for the progressRes button; the row shows its own failure.
	apply(value) {
		const self = this;
		const setting = this.props.setting;
		let parsed;
		try {
			parsed = value !== undefined ? value : parseSettingValue(setting, this.state.draft);
		} catch (err) {
			this.setState({error: err.message});
			return Promise.reject(err);
		}
		this.setState({error: null});
		return this.props.onSet(setting.property, parsed)
			.catch(function (err) {
				self.setState({error: errorMessage(err, 'set ' + setting.property)});
				throw err;
			});
	}

	confirmAction() {
		const self = this;
		const setting = this.props.setting;
		return new Promise(function (resolve, reject) {
			confirmAlert({
				customUI: ({onClose}) => (
					<div className='dialog'>
						<div className={"top-content"}>
							<h2>{setting.label || setting.property}</h2>
							<p>{setting.description || 'Send this command to the device?'}</p>
						</div>
						<div className={"bottom-menu"}>
							{/* Resolves, so backing out does not flash the button as a failure. */}
							<Button label={"Cancel"} color={'gd-grey'} onClick={() => {
								onClose();
								resolve();
							}}/>
							<Button color={'gd-red'} label={"Send"} onClick={() => {
								onClose();
								self.apply(setting.values[0]).then(resolve, reject);
							}}/>
						</div>
					</div>
				)
			});
		});
	}

	renderInput() {
		const self = this;
		const setting = this.props.setting;
		const name = 'zb-setting-' + setting.property;
		const onChange = (e) => self.setState({draft: e.target.value});
		if (setting.type === 'numeric') {
			return <Input type={'number'} name={name} value={this.state.draft} onChange={onChange}
						  InputProps={{min: setting.min, max: setting.max, step: setting.step}}/>;
		}
		if (setting.type === 'enum') {
			return <Dropdown name={name} value={this.state.draft} onChange={onChange}
							 fixeditems={(setting.values || []).map(v => ({title: v, value: v}))}/>;
		}
		if (setting.type === 'binary') {
			return <Dropdown name={name} value={this.state.draft} onChange={onChange}
							 fixeditems={[
								 {title: formatValue(setting.valueOn) || 'On', value: 'on'},
								 {title: formatValue(setting.valueOff) || 'Off', value: 'off'},
							 ]}/>;
		}
		return <Input name={name} value={this.state.draft} onChange={onChange}/>;
	}

	render() {
		const setting = this.props.setting;
		const action = isAction(setting);
		const current = formatValue(setting.value);
		return (
			<div className={'zb-setting'}>
				<div className={'zb-setting-head'}>
					<div>
						<div className={'zb-setting-label'}>{setting.label || setting.property}
							<span className={'zb-setting-property'}>{setting.property}</span></div>
						{setting.description ? <div className={'zb-setting-description'}>{setting.description}</div> : null}
					</div>
					{!action ?
						<div className={'zb-setting-current'}>
							<span className={'zb-label'}>Current</span>
							<span className={'zb-setting-current-value'}>{current !== null ? current
								: setting.readable ? <Unknown/> : <span className={'zb-muted'}>write-only</span>}
								{current !== null && setting.unit ? ' ' + setting.unit : ''}</span>
						</div> : null}
				</div>

				<div className={'zb-setting-controls'}>
					{action ? null :
						<div className={'zb-setting-input'}>
							{this.renderInput()}
							{setting.unit ? <span className={'zb-muted'}>{setting.unit}</span> : null}
						</div>}
					<Button additionalclasses={'sm'} progressRes={true}
							color={action ? 'gd-red' : 'gd-blue'}
							label={action ? (setting.label || setting.values[0]) : 'Set'}
							onClick={action ? this.confirmAction : () => this.apply()}/>
				</div>

				{setting.managed ?
					<div className={'zb-note warn'}>Set by this asset's config; the hub puts the configured value back on its next sync.</div> : null}
				{this.state.error ? <div className={'zb-note bad'}>{this.state.error}</div> : null}
			</div>
		);
	}
}

export function ZigbeeDeviceInfo({device, onSet, onRaw}) {
	let definition = device.definition;
	let lqi = typeof device.linkquality === 'number' ? device.linkquality : null;
	let endpoints = device.endpoints || [];
	// Absent from firmware that predates zigbee.set.
	let settings = device.settings || [];
	let manufacturer = [device.manufacturerName, device.manufacturerID !== undefined ? '(' + hex(device.manufacturerID) + ')' : null]
		.filter(Boolean).join(' ');

	return (
		<div className={'zb-device'}>
			<div className={'zb-summary'}>
				<div>
					<div className={'zb-title'}>
						{definition ? definition.vendor + ' ' + definition.model : (device.modelID || 'Unrecognised device')}
					</div>
					<div className={'zb-subtitle'}>
						{definition ? definition.description : 'No definition matched, so the hub ignores its messages.'}
					</div>
				</div>
				<span className={'zb-pill ' + (INTERVIEW_TONE[device.interviewState] || 'neutral')}>
					Interview {String(device.interviewState || 'unknown').toLowerCase().replace('_', ' ')}
				</span>
			</div>

			<div className={'zb-stats'}>
				<Stat label={'Link quality'} value={lqi} detail={lqi !== null ? 'of 255' : null}>
					{lqi !== null ?
						<div className={'zb-bar'}><div style={{width: Math.min(100, lqi / 255 * 100) + '%'}}/></div> : null}
				</Stat>
				<Stat label={'Last seen'} value={relativeTime(device.lastSeen)}
					  detail={device.lastSeen ? new Date(device.lastSeen).toLocaleString() : null}/>
				<Stat label={'Type'} value={device.type}/>
				<Stat label={'Power source'} value={device.powerSource}/>
			</div>

			<div className={'zb-fields'}>
				<Field label={'IEEE address'} mono>{device.ieeeAddr}</Field>
				<Field label={'Network address'} mono>{typeof device.networkAddress === 'number' ? hex(device.networkAddress) : null}</Field>
				<Field label={'Model ID'}>{device.modelID}</Field>
				<Field label={'Manufacturer'}>{manufacturer}</Field>
			</div>

			{onSet && settings.length > 0 ?
				<div className={'zb-settings'}>
					<div className={'zb-heading'}>Settings <span className={'zb-muted'}>({settings.length})</span></div>
					{/* Keyed on the value too, so a row resets its input when a Refresh brings a new one. */}
					{settings.map(setting => <SettingRow key={setting.property + ':' + formatValue(setting.value)}
														 setting={setting} onSet={onSet}/>)}
				</div> : null}

			{onRaw ? <ZigbeeCustomClusters clusters={device.customClusters} onRaw={onRaw}/> : null}

			<div className={'zb-heading'}>Endpoints <span className={'zb-muted'}>({endpoints.length})</span></div>
			{endpoints.length === 0 ? <span className={'zb-muted'}>No endpoints: the interview has not read them yet.</span> :
				endpoints.map(ep => <Endpoint key={ep.ID} ep={ep}/>)}
		</div>
	);
}

class AssetDebug extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			device: null,
			readAt: null,
			error: null,
		};
		this.refresh = this.refresh.bind(this);
		this.setSetting = this.setSetting.bind(this);
		this.rawCall = this.rawCall.bind(this);
	}

	// Raw ZCL (zigbee.read / write / command) against this asset's device on its gateway.
	rawCall(method, params) {
		return gatewayCommandSocket.invoke(this.props.item.gatewayId, method, {id: this.props.item.code, ...params});
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	// Only on Refresh: opening the tab never talks to the hub. Returns the promise for the progressRes button.
	refresh() {
		const self = this;
		const item = this.props.item;
		this.setState({error: null});
		return gatewayCommandSocket.invoke(item.gatewayId, GET_DEVICE_METHOD, {id: item.code})
			.then(function (device) {
				if (!self.unmounted) self.setState({device, readAt: Date.now()});
			})
			.catch(function (err) {
				if (!self.unmounted) {
					self.setState({error: errorMessage(err, 'read the device from the hub')});
				}
				throw err;
			});
	}

	// Writes one property, then shows what the converters published back; without that confirmation the sent value stands in.
	setSetting(property, value) {
		const self = this;
		const item = this.props.item;
		return gatewayCommandSocket.invoke(item.gatewayId, SET_METHOD, {id: item.code, values: {[property]: value}})
			.then(function (res) {
				if (self.unmounted || !self.state.device) return;
				const state = (res && res.state) || {};
				const settings = (self.state.device.settings || []).map(function (s) {
					if (s.property !== property || isAction(s)) return s;
					return {...s, value: state[property] !== undefined ? state[property] : value};
				});
				self.setState({device: {...self.state.device, settings}});
			});
	}

	render() {
		const self = this;
		return (
			<div className={"page grey narrow not-flex asset-debug"}>
				<div className={'column top'}>
					<div className={'detail-content column'}>
						<Card>
							<CardHeader title={'Zigbee'}/>
							<CardBody>
								<div className={'zb-toolbar'}>
									<p>Read this device's Zigbee record from its hub: identity, endpoints, binds and configured reporting.</p>
									<div className={'zb-toolbar-actions'}>
										<Button additionalclasses={'sm'} onClick={self.refresh} color={'gd-blue'}
												label={'Refresh'} progressRes={true}/>
										{this.state.readAt ?
											<span className={'zb-muted'}>Read at {new Date(this.state.readAt).toLocaleTimeString()}</span> : null}
									</div>
								</div>

								{this.state.error ? <div className={'zb-note bad'}>{this.state.error}</div> : null}

								{this.state.device
									? <ZigbeeDeviceInfo device={this.state.device} onSet={this.setSetting} onRaw={this.rawCall}/>
									: <div className={'zb-empty'}>Press Refresh to read this device from its hub.</div>}
							</CardBody>
						</Card>
					</div>
				</div>
			</div>
		);
	}
}

export default AssetDebug;
